import { useCallback, useMemo } from 'react'
import { useLocation, useNavigate } from 'react-router'

import { useUrlSearchParams } from './use-url-search-params'
import { CurrentSort } from '../Elements/Table'
import { SortingConfirguration } from '../Elements/Table/types'
import { UrlParams } from '../Types'
import { FetchProps, ApiTableColumn } from '../Elements/ApiView'

interface ReturnType<T> {
  urlPage: number | undefined
  updateUrlPage: (newPage: number) => void
  urlFilter: string | undefined
  updateUrlFilter: (newValue: string) => void
  updateUrlSort: (newSort: SortingConfirguration<T>) => void
  parseUrlSort: <T>(sort: string) => SortingConfirguration<T> | undefined
  urlSort: SortingConfirguration<T> | undefined
  urlLimit: number | undefined
  updateUrlLimit: (newLimit?: number) => void
  resetFilters: (initialTableLimit: number) => void
  searchFieldMaxLength: number | undefined
}

interface Props<T> {
  urlParams: UrlParams
  setCurrentPage: ((page: number) => void) | undefined
  tableColumns: ApiTableColumn<T>[]
  totalRecordsCount: number
  initialTablePageLimit: number
  initialTableSort: SortingConfirguration<T> | undefined
  fetchProps: FetchProps
}

interface QueriesData {
  filter: string | null
  page: string | null
  limit: string | null
  sort: string | null
}

const defaultParamsValidationPreferences = {
  defaultLimit: 10,
  filterMaxChars: 30,
  maxLimit: 50,
  minLimit: 3,
  minPage: 0,
}

export const useListUrlParams = <T>({
  urlParams,
  initialTablePageLimit,
  initialTableSort,
  totalRecordsCount,
  setCurrentPage,
  tableColumns,
  fetchProps,
}: Props<T>): ReturnType<T> => {
  const { enabled, customValidationPreferences } = urlParams
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const query = useUrlSearchParams()
  const allowedFilters = tableColumns.map(column => column.name.toString())
  const defaultSort = useMemo(() => {
    if (!initialTableSort) {
      const { orderBy } = fetchProps

      if (orderBy) {
        return orderBy
      }

      return `-${allowedFilters[0]}`
    }

    return (Array.isArray(initialTableSort) ? initialTableSort : [initialTableSort])
      .map(elem => `${elem.direction === 'desc' ? '-' : ''}${elem.name.toString()}`)
      .join(',')
  }, [initialTableSort, fetchProps, allowedFilters])

  const validationPreferences = useMemo(
    () => ({
      ...{
        ...defaultParamsValidationPreferences,
        defaultSort,
      },
      ...customValidationPreferences,
    }),
    [customValidationPreferences, defaultSort]
  )

  /**
   * Return parsed URL params and limit it to the allowed values
   **/
  const {
    page: pageQuery,
    filter: filterQuery,
    limit: limitQuery,
    sort: sortQuery,
  }: QueriesData = useMemo(() => {
    let filterQuery = query.get('filter')
    let pageQuery = query.get('page')
    let limitQuery = query.get('limit')
    let sortQuery = query.get('sort')

    if (!enabled) {
      return {
        limit: limitQuery,
        page: pageQuery,
        filter: filterQuery,
        sort: sortQuery,
      }
    }

    const { defaultLimit, defaultSort, filterMaxChars, maxLimit, minLimit, minPage } = validationPreferences

    if (limitQuery) {
      const limitNumber = parseInt(limitQuery)

      if (isNaN(limitNumber)) {
        limitQuery = defaultLimit.toString()
      } else if (limitNumber < minLimit) {
        limitQuery = minLimit.toString()
      } else if (limitNumber > maxLimit) {
        limitQuery = maxLimit.toString()
      }
    }

    if (pageQuery) {
      const pageNumber = parseInt(pageQuery)
      const limit = limitQuery ? parseInt(limitQuery) : initialTablePageLimit
      const maxPage = totalRecordsCount > 0 ? Math.ceil(totalRecordsCount / limit) - 1 : 0

      if (isNaN(pageNumber) || pageNumber < minPage) {
        pageQuery = minPage.toString()
      } else if (maxPage && pageNumber > maxPage) {
        pageQuery = maxPage.toString()
      }
    }

    if (filterQuery) {
      filterQuery = filterQuery.slice(0, filterMaxChars)
    }

    if (sortQuery) {
      const sortName = sortQuery.slice(1)

      if (!allowedFilters.includes(sortName)) {
        sortQuery = defaultSort
      }
    }

    return {
      limit: limitQuery,
      page: pageQuery,
      filter: filterQuery,
      sort: sortQuery,
    }
  }, [query, enabled, validationPreferences, initialTablePageLimit, totalRecordsCount, allowedFilters])

  const urlPage = enabled && pageQuery ? parseInt(pageQuery) : undefined

  const updateUrlFilter = (newValue: string): void => {
    if (!enabled) return

    if (newValue !== query.get('filter')) {
      query.delete('page')
      setCurrentPage?.(0)
    }

    if (newValue) {
      query.set('filter', newValue.slice(0, validationPreferences.filterMaxChars))
    } else {
      query.delete('filter')
    }

    navigate(`${pathname}?${query.toString()}`)
  }

  const updateUrlPage = useCallback(
    (newPage: number) => {
      if (!enabled) return

      if (newPage === 0) {
        query.delete('page')
      } else {
        query.set('page', newPage.toString())
      }

      navigate(`${pathname}?${query.toString()}`)
    },
    [query, enabled, pathname, navigate]
  )

  const updateUrlSort = <T>(newSort: SortingConfirguration<T> | ''): void => {
    if (!enabled) return

    if (!newSort) {
      query.delete('sort')
    } else {
      const sortArray = Array.isArray(newSort) ? newSort : [newSort]
      const sortStrings = sortArray.map(sortEntry => {
        const direction = sortEntry.direction === 'desc' ? '-' : '+'
        return `${direction}${sortEntry.name.toString()}`
      })

      query.set('sort', sortStrings.join(','))
    }

    navigate(`${pathname}?${query.toString()}`)
  }

  const parseUrlSort = <T>(sort: string): SortingConfirguration<T> | undefined => {
    if (!sort) return undefined
    const sortArray = sort.split(',')

    const sortObjectArray: CurrentSort<T>[] = []

    sortArray.forEach(sortKey => {
      const direction = sortKey.startsWith('-') ? 'desc' : 'asc'
      const name = sortKey.slice(1)

      if (allowedFilters.includes(name)) {
        sortObjectArray.push({
          name: name as keyof T,
          direction,
        })
      }
    })

    if (sortObjectArray.length > 0) {
      return sortObjectArray
    }

    updateUrlSort('')
    return undefined
  }

  const updateUrlLimit = (newLimit?: number): void => {
    if (!enabled) return

    if (newLimit) {
      query.set('limit', newLimit.toString())
    } else {
      query.delete('limit')
    }

    navigate(`${pathname}?${query.toString()}`)
  }

  const resetFilters = (initialTableLimit: number): void => {
    if (!enabled) return

    query.delete('filter')
    query.delete('page')
    query.delete('limit')
    query.delete('sort')

    navigate(`${pathname}?${query.toString()}`)
  }

  const urlSort = enabled && sortQuery ? parseUrlSort(sortQuery) : undefined
  const urlFilter = enabled && filterQuery ? filterQuery : undefined
  const urlLimit = enabled && limitQuery ? parseInt(limitQuery) : undefined

  return {
    updateUrlFilter,
    updateUrlPage,
    urlPage,
    updateUrlSort,
    parseUrlSort,
    urlSort,
    urlFilter,
    updateUrlLimit,
    urlLimit,
    resetFilters,
    searchFieldMaxLength: enabled ? validationPreferences.filterMaxChars : undefined,
  }
}
